<template>
    <div class="mx-0 px-0 py-0 mt-3">
        <v-row no-gutters class="mx-4">
            <v-col cols="12">
                <v-data-table
                    disable-pagination
                    hide-default-footer
                    :headers="headers"
                    :items="filteredQuotes"
                    class="elevation-0 clickable-row"
                    :mobile-breakpoint="0"
                    :loading="loading"
                    :height="$vuetify.breakpoint.mdAndDown ? '75vh' : '63.4vh'"
                    @click:row="openQuote"
                    fixed-header
                >
                    <template v-slot:top>
                        <v-toolbar
                            flat
                            :style="{ 'background-color': '#eeeeee' }"
                        >
                            <v-toolbar-title
                                >CLIENT APPROVED QUOTES</v-toolbar-title
                            >
                            <v-spacer></v-spacer>
                            <v-text-field
                                v-model="search"
                                append-icon="mdi-magnify"
                                label="Search"
                                single-line
                                hide-details
                                class="mr-4"
                                style="max-width: 300px;"
                            ></v-text-field>
                        </v-toolbar>
                    </template>

                    <!-- Reference -->
                    <template v-slot:[`item.number`]="{ item }">
                        <div class="my-4">
                            {{ item.number }}
                        </div>
                    </template>

                    <!-- Name -->
                    <template v-slot:[`item.name`]="{ item }">
                        <div class="my-4">
                            {{ item.name }}
                        </div>
                    </template>

                    <!-- Client -->
                    <template v-slot:[`item.client`]="{ item }">
                        <div class="my-4">
                            {{ getClientName(item.clientId) }}
                        </div>
                    </template>

                    <!-- Price -->
                    <template v-slot:[`item.totalCosts`]="{ item }">
                        <div class="my-4" :style="{ 'text-align': 'end' }">
                            {{ formatCurrency(item) }}
                        </div>
                    </template>

                    <!-- Sales Validation -->
                    <template v-slot:[`item.salesValidation`]="{ item }">
                        <div
                            class="my-4 d-flex justify-center align-center"
                            @click.stop
                        >
                            <v-checkbox
                                :key="
                                    `sales-v-${item.id}-${salesValidationRenderKey}`
                                "
                                :input-value="false"
                                @change="onSalesValidationAction(item, $event)"
                                hide-details
                                color="primary"
                                class="pa-0 ma-0 mr-1"
                                :disabled="
                                    loading ||
                                        !areDocumentsValid(item) ||
                                        !userPermissions.includes(
                                            'salesValidation'
                                        )
                                "
                            />
                            <v-btn
                                icon
                                small
                                class="ml-1"
                                @click.stop="openAwardAttachment(item)"
                                :disabled="loading || !hasAwardAttachment(item)"
                            >
                                <v-icon small color="primary"
                                    >mdi-paperclip</v-icon
                                >
                            </v-btn>
                            <!-- <v-btn
                                icon
                                small
                                class="ml-1"
                                @click.stop="openQuote(item)"
                                :disabled="
                                    loading || !hasAwardAttachment(item)
                                "
                            >
                                <v-icon small color="primary">mdi-eye</v-icon>
                            </v-btn> -->
                        </div>
                    </template>

                    <template
                        v-for="docCol in docValidationColumns"
                        v-slot:[`item.${docCol.slotKey}`]="{ item }"
                    >
                        <div
                            :key="docCol.slotKey"
                            class="my-4 doc-validation-cell"
                        >
                            <v-btn
                                v-if="
                                    isFileLoaded(
                                        getClientDocument(
                                            item.clientId,
                                            docCol.key
                                        )
                                    )
                                "
                                icon
                                x-small
                                class="doc-link-btn"
                                :style="{
                                    borderBottom: `1px solid ${
                                        normalizeDocumentStatus(
                                            getClientDocument(
                                                item.clientId,
                                                docCol.key
                                            )
                                        ) === 'REJECTED'
                                            ? '#d32f2f'
                                            : '#2e7d32'
                                    }`,
                                }"
                                @click.stop="
                                    openQuoteFile(
                                        getClientDocument(
                                            item.clientId,
                                            docCol.key
                                        ),
                                        item
                                    )
                                "
                            >
                                <v-icon small color="primary">
                                    mdi-paperclip
                                </v-icon>
                            </v-btn>
                            <div
                                v-if="
                                    !isClientRequiredFor(item.clientId, docCol)
                                "
                                class="doc-status-slot"
                            >
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                            v-bind="attrs"
                                            v-on="on"
                                            color="success"
                                            small
                                            class="doc-status-icon"
                                        >
                                            mdi-check-circle
                                        </v-icon>
                                    </template>
                                    <span>Not required for this client</span>
                                </v-tooltip>
                            </div>
                            <div
                                v-if="
                                    normalizeDocumentStatus(
                                        getClientDocument(
                                            item.clientId,
                                            docCol.key
                                        )
                                    ) === 'REJECTED'
                                "
                                class="doc-info-slot"
                            >
                                <v-btn
                                    icon
                                    x-small
                                    @click.stop="
                                        openRejectionInfo(
                                            getClientDocument(
                                                item.clientId,
                                                docCol.key
                                            ).rejectionNotes
                                        )
                                    "
                                >
                                    <v-icon small color="info">
                                        mdi-information-outline
                                    </v-icon>
                                </v-btn>
                            </div>
                            <div
                                v-if="
                                    isClientRequiredFor(
                                        item.clientId,
                                        docCol
                                    ) &&
                                        isFileLoaded(
                                            getClientDocument(
                                                item.clientId,
                                                docCol.key
                                            )
                                        )
                                "
                                class="d-inline-flex align-center"
                                style="gap: 4px"
                                @click.stop
                            >
                                <v-tooltip
                                    v-if="
                                        canShowApprove(
                                            getClientDocument(
                                                item.clientId,
                                                docCol.key
                                            )
                                        )
                                    "
                                    :key="
                                        `approve-tooltip-${docCol.key}-${item.clientId}-${validationRenderKey}`
                                    "
                                    top
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <span v-bind="attrs" v-on="on">
                                            <v-checkbox
                                                :key="
                                                    `approve-${docCol.key}-${item.clientId}-${validationRenderKey}`
                                                "
                                                :input-value="
                                                    getFileApprovalStatusForDisplay(
                                                        getClientDocument(
                                                            item.clientId,
                                                            docCol.key
                                                        )
                                                    )
                                                "
                                                @change="
                                                    handleApproveChange(
                                                        item,
                                                        docCol.key,
                                                        getClientDocument(
                                                            item.clientId,
                                                            docCol.key
                                                        ),
                                                        $event
                                                    )
                                                "
                                                hide-details
                                                color="primary"
                                                small
                                                class="align-center pa-0 ma-0"
                                                :disabled="
                                                    !isFileLoaded(
                                                        getClientDocument(
                                                            item.clientId,
                                                            docCol.key
                                                        )
                                                    ) ||
                                                        !userPermissions.includes(
                                                            'documentValidation'
                                                        )
                                                "
                                            ></v-checkbox>
                                        </span>
                                    </template>
                                    <span>Approve</span>
                                </v-tooltip>
                                <v-tooltip
                                    v-if="
                                        canShowReject(
                                            getClientDocument(
                                                item.clientId,
                                                docCol.key
                                            )
                                        )
                                    "
                                    :key="
                                        `reject-tooltip-${docCol.key}-${item.clientId}-${validationRenderKey}`
                                    "
                                    top
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            x-small
                                            :color="
                                                getRejectButtonColor(
                                                    getClientDocument(
                                                        item.clientId,
                                                        docCol.key
                                                    )
                                                )
                                            "
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="
                                                !isFileLoaded(
                                                    getClientDocument(
                                                        item.clientId,
                                                        docCol.key
                                                    )
                                                ) ||
                                                    !userPermissions.includes(
                                                        'documentValidation'
                                                    )
                                            "
                                            @click.stop="
                                                openRejectDialog(
                                                    item,
                                                    docCol.key,
                                                    getClientDocument(
                                                        item.clientId,
                                                        docCol.key
                                                    )
                                                )
                                            "
                                            class="ml-n2"
                                        >
                                            <v-icon small>mdi-cancel</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Reject</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </template>

                    <template v-slot:[`item.sapClient`]="{ item }">
                        <div
                            class="my-4 d-flex align-center justify-center"
                            @click.stop
                        >
                            <v-checkbox
                                :key="`sap-c-${item.id}-${sapRenderKey}`"
                                :input-value="!!item.sapClientApproved"
                                @change="
                                    handleSapCheck(
                                        item,
                                        'sapClientApproved',
                                        $event
                                    )
                                "
                                hide-details
                                :disabled="
                                    loading ||
                                        !userPermissions.includes(
                                            'validationSAP'
                                        )
                                "
                            />
                        </div>
                    </template>

                    <template v-slot:[`item.projectSap`]="{ item }">
                        <div
                            class="my-4 d-flex align-center justify-center"
                            @click.stop
                        >
                            <v-checkbox
                                :key="`sap-p-${item.id}-${sapRenderKey}`"
                                :input-value="!!item.projectSapApproved"
                                @change="
                                    handleSapCheck(
                                        item,
                                        'projectSapApproved',
                                        $event
                                    )
                                "
                                hide-details
                                :disabled="
                                    loading ||
                                        !userPermissions.includes(
                                            'validationSAP'
                                        )
                                "
                            />
                            <v-tooltip
                                v-if="
                                    item.sapClientApproved &&
                                        item.projectSapApproved
                                "
                                top
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        x-small
                                        class="ml-1"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click.stop="openSapDialog(item)"
                                        :disabled="
                                            loading ||
                                                !userPermissions.includes(
                                                    'validationSAP'
                                                )
                                        "
                                    >
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <div class="text-caption">
                                    <div>
                                        <strong>Offer:</strong>
                                        {{ item.offer || '—' }}
                                    </div>
                                    <div>
                                        <strong>SAP Code:</strong>
                                        {{ item.sapCode || '—' }}
                                    </div>
                                </div>
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- Actions -->
                    <!-- <template v-slot:[`item.actions`]="{ item }">
                        <div class="d-flex justify-center my-4">
                            <v-btn
                                icon
                                small
                                @click.stop="handleQuoteAwarded(item)"
                                :disabled="loading"
                            >
                                <v-icon small color="primary">mdi-link</v-icon>
                            </v-btn>
                        </div>
                    </template> -->
                </v-data-table>
            </v-col>
        </v-row>

        <!-- Quote Dialog -->
        <v-dialog
            :retain-focus="false"
            v-model="dialogQuote"
            fullscreen
            ref="dialogQuote"
        >
            <v-card class="fill-height">
                <v-card-title class="ml-n4">
                    <v-btn icon @click="closeQuote">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Quote
                </v-card-title>
                <v-card-text
                    class="px-0"
                    :style="{ height: `${heightPopUp}px` }"
                >
                    <Quote
                        v-if="selectedQuote"
                        :id="selectedQuote.id"
                        :quote="selectedQuote"
                        :settings="settings"
                        :openPreviewQuote="true"
                        :history="!!selectedQuote.archive"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Confirm Awarded Dialog -->
        <v-dialog
            :retain-focus="false"
            v-model="confirmAwardedDialog"
            persistent
            max-width="600px"
        >
            <ConfirmAwarded
                v-if="confirmAwardedDialog && selectedQuote"
                :quote="selectedQuote"
                @close="closeConfirmAwarded"
                @updateChanges="handleQuoteUpdated"
            />
        </v-dialog>

        <!-- Confirmation Dialog for Sales Validation -->
        <v-dialog
            v-model="confirmSalesValidationDialog"
            persistent
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Confirm Sales Validation
                </v-card-title>
                <v-card-text>
                    Are you sure you want to
                    {{ pendingSalesValidation ? 'approve' : 'reject' }} sales
                    validation for this quote?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="secondary"
                        @click="confirmSalesValidationDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="confirmSalesValidation"
                        :loading="loading"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Confirmation Dialog for Document Validation -->
        <v-dialog
            v-model="confirmDocumentValidationDialog"
            persistent
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Confirm Document Validation
                </v-card-title>
                <v-card-text>
                    Are you sure you want to
                    {{
                        pendingDocumentAction === 'REJECT'
                            ? 'reject'
                            : 'approve'
                    }}
                    {{ pendingFileTypeLabel }}?
                    <v-textarea
                        v-if="pendingDocumentAction === 'REJECT'"
                        v-model="pendingRejectionNotes"
                        label="Rejection reasons"
                        outlined
                        dense
                        auto-grow
                        rows="2"
                        class="mt-4"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="secondary"
                        @click="cancelDocumentValidation"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="confirmDocumentValidation"
                        :loading="loading"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="sapDialog" persistent max-width="480px">
            <v-card>
                <v-card-title class="text-h5">SAP Offer and Code</v-card-title>
                <v-card-text>
                    <v-text-field v-model="sapOffer" label="Offer" />
                    <v-text-field v-model="sapCode" label="Code SAP" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="secondary" @click="closeSapDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="saveSapData"
                        :loading="loading"
                    >
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rejectionInfoDialog" max-width="420px">
            <v-card>
                <v-card-title class="text-h6">Rejection reasons</v-card-title>
                <v-card-text>
                    <v-textarea
                        :value="rejectionInfoText"
                        readonly
                        outlined
                        hide-details
                        rows="4"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="primary"
                        @click="rejectionInfoDialog = false"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog max-width="300px" v-model="rejected">
            <v-card :loading="loadingArchive">
                <v-card-title class="text-h5">Lost Quote</v-card-title>
                <v-card-text>
                    Are you sure you want to change the status of this quote?

                    <v-form ref="formReason" v-model="valid" class="mt-7">
                        <v-autocomplete
                            v-model="rejectReason"
                            :items="reasons"
                            :rules="[rules.required]"
                            prepend-icon="mdi-text"
                            item-text="reason"
                            item-value="id"
                            label="Reason*"
                            required
                        />
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-btn text color="secondary" @click="rejected = false">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="rejectedQuote"
                        :disabled="!valid || loadingArchive"
                        text
                        color="error"
                        :loading="loadingArchive"
                    >
                        Lost
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            :retain-focus="false"
            v-model="quoteFormDialog"
            persistent
            max-width="500px"
        >
            <QuoteModifications
                v-if="quoteFormDialog"
                :originalQuote="modificationQuote"
                :settings="settings"
                :loading2="loading2"
                @closeDialog="closeQuoteModifications"
                @modificationRequest="modificationRequest"
            />
        </v-dialog>
    </div>
</template>

<script>
import API from '@/services/api'
import _ from 'lodash'
import { mapMutations, mapActions } from 'vuex'
import { openFile } from '@/services/storage/openFile.js'
import { storage } from '@/services/firebase'

export default {
    name: 'ApprovedQuotes',
    components: {
        Quote: () => import('@/views/Quotes/Quote.vue'),
        ConfirmAwarded: () => import('@/components/Quotes/ConfirmAwarded.vue'),
        QuoteModifications: () =>
            import('@/components/Quotes/QuoteModifications'),
    },
    data: () => ({
        loading: true,
        quotes: [],
        clients: [],
        specialDocuments: {},
        settings: [],
        search: '',
        dialogQuote: false,
        selectedQuote: null,
        height: 0,
        heightPopUp: 0,
        confirmAwardedDialog: false,
        quoteFormDialog: false,
        modificationQuote: {},
        loading2: false,
        rejected: false,
        loadingArchive: false,
        quoteToArchive: {},
        rejectReason: null,
        reasons: [],
        valid: false,
        rules: {
            required: v => !!v || 'Required',
        },
        confirmSalesValidationDialog: false,
        confirmDocumentValidationDialog: false,
        pendingSalesValidation: null,
        pendingDocumentValidation: null,
        pendingFileType: null,
        pendingDocumentAction: null,
        pendingRejectionNotes: '',
        validationRenderKey: 0,
        salesValidationRenderKey: 0,
        pendingQuote: null,
        pendingFile: null,
        companyId: '',
        userId: '',
        userPermissions: [],
        documentTypes: [],
        sapDialog: false,
        sapDialogItem: null,
        sapOffer: '',
        sapCode: '',
        sapRenderKey: 0,
        rejectionInfoDialog: false,
        rejectionInfoText: '',
    }),
    provide() {
        return {
            replaceQuote: this.replaceQuote,
            archiveQuote: this.archiveQuote,
            backToMainRoute: this.backToMainRoute,
            rejectedDialog: this.rejectedDialog,
            quoteAwarded: this.quoteAwarded,
            modificationRequestProcess: this.modificationRequestProcess,
        }
    },
    computed: {
        filteredQuotes() {
            if (!this.search) {
                return this.quotes
            }
            const searchLower = this.search.toLowerCase()
            return this.quotes.filter(quote => {
                const clientName =
                    quote.client?.name ||
                    this.getClientName(quote.clientId) ||
                    ''
                const quoteName = quote.name || ''
                const quoteNumber = quote.number?.toString() || ''
                return (
                    clientName.toLowerCase().includes(searchLower) ||
                    quoteName.toLowerCase().includes(searchLower) ||
                    quoteNumber.includes(searchLower)
                )
            })
        },
        docValidationColumns() {
            return this.documentTypes.map(doc => ({
                ...doc,
                slotKey: `validation_${doc.key}`,
            }))
        },
        headers() {
            const docCols = this.docValidationColumns.map(col => ({
                text: col.label,
                value: col.slotKey,
                align: 'center',
                sortable: false,
                width: '140px',
            }))
            return [
                {
                    text: 'Reference',
                    value: 'number',
                    align: 'center',
                    sortable: true,
                },
                {
                    text: 'Name',
                    value: 'name',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Client',
                    value: 'client',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Price',
                    value: 'totalCosts',
                    align: 'right',
                    sortable: true,
                },
                {
                    text: 'Sales Validation',
                    value: 'salesValidation',
                    align: 'center',
                    sortable: false,
                    width: '120px',
                },
                ...docCols,
                {
                    text: 'SAP Client',
                    value: 'sapClient',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Project SAP',
                    value: 'projectSap',
                    align: 'left',
                    sortable: false,
                },
            ]
        },
        pendingFileTypeLabel() {
            if (!this.pendingFileType) return ''
            const docType =
                this.documentTypes.find(d => d.key === this.pendingFileType) ||
                null
            return docType ? docType.label : this.pendingFileType
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.companyId = user.companyId
            this.userPermissions = user.permissions || []
            await this.loadData()
            this.userId = JSON.parse(localStorage.getItem('userId'))
            this.onResize()
            window.addEventListener('resize', this.onResize)
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
    },
    methods: {
        ...mapMutations(['setErrorItems', 'setClientQuote']),
        ...mapActions(['replaceQuoteAction', 'removeQuoteAction']),
        sortQuotesByUpdatedOn(quotes = []) {
            return [...quotes].sort((a, b) => {
                const aSeconds =
                    a?.updatedOn?._seconds || a?.updatedOn?.seconds || 0
                const bSeconds =
                    b?.updatedOn?._seconds || b?.updatedOn?.seconds || 0
                return bSeconds - aSeconds
            })
        },
        rebuildSpecialDocuments(documentsByType = {}) {
            const indexes = {}
            for (const docKey of Object.keys(documentsByType)) {
                indexes[docKey] = new Map(
                    (documentsByType[docKey] || []).map(doc => [doc.id, doc])
                )
            }

            const linkedDocId = ref => {
                if (typeof ref === 'string') return ref.trim()
                if (ref && typeof ref.documentId === 'string')
                    return ref.documentId.trim()
                return ''
            }

            const specialDocuments = {}
            for (const client of this.clients || []) {
                if (!client?.id) continue

                const next = {}
                for (const docCol of this.docValidationColumns) {
                    const docId = linkedDocId(client[docCol.key])
                    if (!docId) continue
                    const map = indexes[docCol.key]
                    if (!map) continue
                    const found = map.get(docId)
                    if (found && found.clientId === client.id) {
                        next[docCol.key] = found
                    }
                }

                if (Object.keys(next).length > 0) {
                    specialDocuments[client.id] = next
                }
            }
            this.specialDocuments = specialDocuments
        },
        async loadData() {
            try {
                const {
                    data: { settings },
                } = await API.getSettings()
                const clientSetting = settings.find(x => x.name == 'Client')
                if (clientSetting?.id) {
                    const formats = await API.getClientSalesFormats(
                        clientSetting.id
                    )
                    this.documentTypes = formats.documentTypes || []
                }
                const {
                    data: { clients },
                } = await API.getClients()
                this.clients = clients
                const docKeys = this.docValidationColumns.map(c => c.key)
                const docsByType = await Promise.all(
                    docKeys.map(key => API.getDocumentsByType(key))
                )
                const documentsByType = {}
                docKeys.forEach((key, idx) => {
                    documentsByType[key] = docsByType[idx] || []
                })
                this.rebuildSpecialDocuments(documentsByType)
                const quotes = await API.getClientApprovedQuotes()
                this.quotes = this.sortQuotesByUpdatedOn(quotes)
                this.quotes.forEach(quote => {
                    if (quote.salesValidation === undefined) {
                        this.$set(quote, 'salesValidation', false)
                    }
                    if (quote.sapClientApproved === undefined) {
                        this.$set(quote, 'sapClientApproved', false)
                    }
                    if (quote.projectSapApproved === undefined) {
                        this.$set(quote, 'projectSapApproved', false)
                    }
                    if (quote.files) {
                        quote.files.forEach(file => {
                            if (file.approved === undefined) {
                                this.$set(file, 'approved', false)
                            }
                        })
                    }
                })

                let orderSettings = settings.filter(x => x.name == 'Company')
                orderSettings[1] = settings.find(x => x.name == 'Exchange')
                orderSettings[2] = settings.find(x => x.name == 'Permissions')
                orderSettings[3] = settings.find(x => x.name == 'Quotes')
                this.settings = orderSettings
                const quotesCfg = settings.find(x => x.name == 'Quotes')
                if (quotesCfg && quotesCfg.rejectionReasons) {
                    this.reasons = quotesCfg.rejectionReasons
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        onSalesValidationAction(item, value) {
            if (!this.userPermissions.includes('salesValidation')) {
                this.salesValidationRenderKey += 1
                return
            }
            if (value !== true) {
                this.salesValidationRenderKey += 1
                return
            }

            this.selectedQuote = item
            this.confirmAwardedDialog = true
            this.salesValidationRenderKey += 1
        },
        confirmSalesValidation() {
            if (!this.selectedQuote) return
            this.confirmSalesValidationDialog = false
            this.confirmAwardedDialog = true
        },
        handleApproveChange(item, fileType, file, value) {
            if (value !== true) {
                this.validationRenderKey += 1
                return
            }
            this.pendingQuote = item
            this.pendingFileType = fileType
            this.pendingFile = file
            this.pendingDocumentAction = 'APPROVE'
            this.pendingDocumentValidation = true
            this.pendingRejectionNotes = ''
            this.confirmDocumentValidationDialog = true
        },
        openRejectDialog(item, fileType, file) {
            this.pendingQuote = item
            this.pendingFileType = fileType
            this.pendingFile = file
            this.pendingDocumentAction = 'REJECT'
            this.pendingDocumentValidation = false
            this.pendingRejectionNotes = ''
            this.confirmDocumentValidationDialog = true
        },
        cancelDocumentValidation() {
            this.confirmDocumentValidationDialog = false
            this.pendingQuote = null
            this.pendingFile = null
            this.pendingFileType = null
            this.pendingDocumentValidation = null
            this.pendingDocumentAction = null
            this.pendingRejectionNotes = ''
            this.validationRenderKey += 1
        },
        async confirmDocumentValidation() {
            try {
                this.loading = true
                if (
                    this.pendingDocumentAction === 'REJECT' &&
                    !this.pendingRejectionNotes.trim()
                ) {
                    throw new Error('Rejection reasons are required')
                }

                const {
                    client: updatedClient,
                } = await API.updateClientDocumentValidation({
                    clientId: this.pendingQuote.clientId,
                    fileType: this.pendingFileType,
                    approved: this.pendingDocumentValidation,
                    rejectionNotes:
                        this.pendingDocumentAction === 'REJECT'
                            ? this.pendingRejectionNotes
                            : '',
                    quoteId: this.pendingQuote.id,
                })

                const clientIndex = this.clients.findIndex(
                    c => c.id === updatedClient.id
                )
                if (clientIndex >= 0) {
                    this.$set(this.clients, clientIndex, updatedClient)
                }
                const docKeys = this.docValidationColumns.map(c => c.key)
                const docsByType = await Promise.all(
                    docKeys.map(key => API.getDocumentsByType(key))
                )
                const documentsByType = {}
                docKeys.forEach((key, idx) => {
                    documentsByType[key] = docsByType[idx] || []
                })
                this.rebuildSpecialDocuments(documentsByType)

                this.validationRenderKey += 1
                this.confirmDocumentValidationDialog = false
                this.pendingQuote = null
                this.pendingFile = null
                this.pendingFileType = null
                this.pendingDocumentValidation = null
                this.pendingDocumentAction = null
                this.pendingRejectionNotes = ''
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                this.validationRenderKey += 1
            } finally {
                this.loading = false
            }
        },
        getFileApprovalStatus(file) {
            if (typeof file === 'object') {
                return file.approved === true
            }
            return false
        },
        normalizeDocumentStatus(file) {
            if (!file || typeof file !== 'object') return 'PENDING'
            if (file.status === 'APPROVED') return 'APPROVED'
            if (file.status === 'REJECTED') return 'REJECTED'
            if (file.status === 'PENDING') return 'PENDING'
            if (file.approved === true) return 'APPROVED'
            return 'PENDING'
        },
        canShowApprove(file) {
            return (
                !!file &&
                typeof file === 'object' &&
                this.isFileLoaded(file) &&
                !this.isDocumentExpired(file)
            )
        },
        canShowReject(file) {
            const status = this.normalizeDocumentStatus(file)
            return status === 'PENDING' || status === 'APPROVED'
        },
        getRejectButtonColor(file) {
            return this.normalizeDocumentStatus(file) === 'APPROVED'
                ? 'grey darken-1'
                : 'error'
        },
        isDocumentExpired(file) {
            if (!file || !file.expirationDate) return false
            const d = file.expirationDate
            const ms =
                d._seconds != null
                    ? d._seconds * 1000
                    : d.seconds != null
                    ? d.seconds * 1000
                    : new Date(d).getTime()
            return ms < Date.now()
        },
        getFileApprovalStatusForDisplay(file) {
            if (this.isDocumentExpired(file)) return false
            return this.getFileApprovalStatus(file)
        },
        isDocValidForQuote(file) {
            if (!this.isFileLoaded(file)) return false
            if (this.isDocumentExpired(file)) return false
            return this.getFileApprovalStatus(file)
        },
        areDocumentsValid(item) {
            const clientType = this.getClientType(item.clientId)
            const requiredDocs = this.docValidationColumns.filter(
                d => d.clientType === clientType
            )
            if (requiredDocs.length === 0) return true
            return requiredDocs.every(docType => {
                const file = this.getClientDocument(item.clientId, docType.key)
                return this.isDocValidForQuote(file)
            })
        },
        async openQuoteFile(file, quote) {
            try {
                const fileName =
                    typeof file === 'object' ? file.file || file.name : file
                const path = `${this.companyId}/clients_documents/${quote.clientId}`
                const storageRef = storage()
                    .ref(path)
                    .child(fileName)
                const url = await storageRef.getDownloadURL()
                window.open(url, '_blank')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error opening file',
                })
            }
        },
        openQuote(quote) {
            this.setClientQuote(quote.id)
            this.selectedQuote = quote
            this.dialogQuote = true
        },
        closeQuote() {
            this.dialogQuote = false
            if (!this.quoteFormDialog) {
                this.selectedQuote = null
            }
        },
        backToMainRoute() {
            this.closeQuote()
        },
        replaceQuote({ status, quote }) {
            if (!quote || !quote.id) return
            const i = this.quotes.findIndex(q => q.id === quote.id)
            if (i >= 0 && status === 'clientApproved') {
                this.$set(this.quotes, i, { ...this.quotes[i], ...quote })
            }
            if (
                this.selectedQuote &&
                this.selectedQuote.id === quote.id &&
                status === 'clientApproved'
            ) {
                this.selectedQuote = { ...this.selectedQuote, ...quote }
            }
        },
        closeSendQuote() {},
        addQuoteForApproval() {},
        updateCosts() {},
        modificationRequestProcess(quote) {
            this.modificationQuote = _.cloneDeep(quote)
            this.quoteFormDialog = true
        },
        closeQuoteModifications() {
            this.quoteFormDialog = false
            this.modificationQuote = {}
            if (!this.dialogQuote) {
                this.selectedQuote = null
            }
        },
        async modificationRequest({
            quoteUpdates,
            message,
            files,
            userToEdit = null,
        }) {
            try {
                this.loading2 = true
                const { id } = _.cloneDeep(this.modificationQuote)

                if (quoteUpdates.city) {
                    if (typeof quoteUpdates.city == 'object') {
                        quoteUpdates.city = quoteUpdates.city.text
                    }
                }
                quoteUpdates.files = files || []
                Object.keys(quoteUpdates).forEach(key => {
                    if (
                        quoteUpdates[key] === null ||
                        quoteUpdates[key] === undefined
                    ) {
                        delete quoteUpdates[key]
                    }
                })
                const {
                    data: { quote: updated },
                } = await API.modificationRequest({
                    id: this.modificationQuote.id,
                    quoteUpdates: {
                        ...quoteUpdates,
                        userToEdit: userToEdit,
                    },
                })

                if (!updated.generatedByClient) {
                    const keys = Object.keys(quoteUpdates)
                    let keyString = ''
                    keys.forEach((key, index) => {
                        keyString += key
                        if (index < keys.length - 1) {
                            keyString += ', '
                        }
                    })
                    await API.createPost({
                        chatId: this.modificationQuote.id,
                        userId: this.userId,
                        message: `New modification request ${
                            keyString.length > 0
                                ? ` updating: ${keyString}`
                                : ''
                        }. ${message}`,
                    })
                }

                this.replaceQuoteAction({ id, quote: updated })
                await this.loadData()
                this.closeQuoteModifications()
                this.closeQuote()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading2 = false
            }
        },
        rejectedDialog(quote) {
            this.rejected = true
            this.quoteToArchive = _.cloneDeep(quote)
        },
        async rejectedQuote() {
            try {
                const status = this.quoteToArchive.status
                const { id } = this.quoteToArchive
                if (status === 'clientApproved') {
                    const index = this.quotes.findIndex(x => x.id === id)
                    if (index >= 0) {
                        this.quotes.splice(index, 1)
                    }
                }
                this.quoteToArchive.status = 'LOST'
                this.loadingArchive = true
                await API.markQuoteAsLost(
                    id,
                    this.quoteToArchive.status,
                    this.rejectReason,
                    'FMGcnvQw2J1b4kpidwr4'
                )
                this.removeQuoteAction(id)
                this.rejectReason = null
                this.closeQuote()
                await this.loadData()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingArchive = false
                this.rejected = false
            }
        },
        async archiveQuote(quote) {
            try {
                if (quote && quote.id) {
                    this.quoteToArchive = _.cloneDeep(quote)
                }
                const status = this.quoteToArchive.status
                const { id } = this.quoteToArchive
                if (status === 'clientApproved') {
                    const index = this.quotes.findIndex(x => x.id === id)
                    if (index >= 0) {
                        this.quotes.splice(index, 1)
                    }
                }
                this.loadingArchive = true
                await API.createQuoteHistory(
                    id,
                    undefined,
                    undefined,
                    'pFDh25EcR74EMiuypq8M'
                )
                this.removeQuoteAction(id)
                this.closeQuote()
                await this.loadData()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingArchive = false
            }
        },
        async reOpenQuote(quote) {
            try {
                this.loading = true
                const { id } = _.cloneDeep(quote)
                await API.updateQuote(id, {
                    notificationId: 'LrubMkCSfpNwEoXemQKL',
                    status: 'OPEN',
                })
                const index = this.quotes.findIndex(q => q.id === id)
                if (index >= 0) {
                    this.quotes.splice(index, 1)
                }
                this.replaceQuoteAction({
                    id,
                    quote: Object.assign({}, quote, { status: 'OPEN' }),
                })
                this.removeQuoteAction(id)
                this.closeQuote()
                await this.loadData()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        quoteAwarded(quote) {
            this.handleQuoteAwarded(quote)
        },
        handleQuoteAwarded(quote) {
            this.selectedQuote = quote
            this.confirmAwardedDialog = true
        },
        closeConfirmAwarded() {
            this.confirmAwardedDialog = false
            this.selectedQuote = null
            this.salesValidationRenderKey += 1
        },
        async handleQuoteUpdated() {
            // Reload data after quote is associated with project
            await this.loadData()
            this.closeConfirmAwarded()
        },
        hasAwardAttachment(item) {
            const action = item?.awardInProgress?.action
            if (action === 'SIGNED') return !!item?.signature
            if (action === 'PO') return !!item?.po
            return false
        },
        getStorageRefFromPath(path) {
            if (!path || typeof path !== 'string') return null
            const clean = path.replace(/^\/+/, '')
            const idx = clean.lastIndexOf('/')
            if (idx < 0) return null
            return {
                folder: clean.slice(0, idx),
                file: clean.slice(idx + 1),
            }
        },
        async openAwardAttachment(item) {
            try {
                const action = item?.awardInProgress?.action
                let rawPath = ''

                if (action === 'SIGNED') {
                    rawPath =
                        typeof item.signature === 'string'
                            ? item.signature
                            : item.signature?.path
                } else if (action === 'PO') {
                    rawPath =
                        typeof item.po === 'string' ? item.po : item.po?.path
                }

                const ref = this.getStorageRefFromPath(rawPath)
                if (!ref) return

                await openFile(ref.file, ref.folder)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error opening attachment',
                })
            }
        },
        formatCurrency(item) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: item.currency || 'USD',
                minimumFractionDigits: 0,
            }).format(item.totalCosts ? item.totalCosts.toFixed(0) : 0)
        },
        getClientName(clientId) {
            if (!clientId || !this.clients.length) return ''
            const client = this.clients.find(c => c.id === clientId)
            return client ? client.name : ''
        },
        getClientById(clientId) {
            if (!clientId || !this.clients.length) return null
            return this.clients.find(c => c.id === clientId) || null
        },
        getClientDocument(clientId, docKey) {
            return this.specialDocuments?.[clientId]?.[docKey] ?? null
        },
        isClientRequiredFor(clientId, docCol) {
            return this.getClientType(clientId) === docCol.clientType
        },
        getClientType(clientId) {
            return this.isClientNational(clientId)
                ? 'national'
                : 'international'
        },
        isFileLoaded(file) {
            return !!(file && (file.file || file.name))
        },
        truncateFileName(name, max = 25) {
            const value = (name || '').toString()
            if (value.length <= max) return value
            return `${value.slice(0, max)}...`
        },
        openRejectionInfo(notes) {
            this.rejectionInfoText = notes || '—'
            this.rejectionInfoDialog = true
        },
        isClientNational(clientId) {
            const client = this.getClientById(clientId)
            const country = (client?.country || '')
                .toString()
                .trim()
                .toLowerCase()
            return country === 'colombia'
        },
        onResize() {
            this.height = window.innerHeight - 200
            this.heightPopUp = window.innerHeight - 62
        },
        async handleSapCheck(item, field, value) {
            try {
                this.loading = true
                const quote = await API.updateSapCheck({
                    quoteId: item.id,
                    field,
                    value,
                })
                const idx = this.quotes.findIndex(q => q.id === item.id)
                if (idx >= 0) {
                    this.$set(this.quotes, idx, {
                        ...this.quotes[idx],
                        ...quote,
                    })
                }
                const merged = { ...item, ...quote }
                const noOffer =
                    merged.offer === undefined ||
                    merged.offer === null ||
                    merged.offer === ''
                const noCode =
                    merged.sapCode === undefined ||
                    merged.sapCode === null ||
                    merged.sapCode === ''
                if (
                    merged.sapClientApproved &&
                    merged.projectSapApproved &&
                    noOffer &&
                    noCode
                ) {
                    const row = idx >= 0 ? this.quotes[idx] : null
                    if (row) this.openSapDialog(row)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                this.sapRenderKey += 1
            } finally {
                this.loading = false
            }
        },
        openSapDialog(item) {
            this.sapDialogItem = item
            this.sapOffer = item.offer != null ? String(item.offer) : ''
            this.sapCode = item.sapCode != null ? String(item.sapCode) : ''
            this.sapDialog = true
        },
        closeSapDialog() {
            this.sapDialog = false
            this.sapDialogItem = null
            this.sapOffer = ''
            this.sapCode = ''
        },
        async saveSapData() {
            if (!this.sapDialogItem) return
            try {
                this.loading = true
                const quote = await API.updateSapData({
                    quoteId: this.sapDialogItem.id,
                    offer: this.sapOffer,
                    sapCode: this.sapCode,
                })
                const idx = this.quotes.findIndex(
                    q => q.id === this.sapDialogItem.id
                )
                if (idx >= 0) {
                    this.$set(this.quotes, idx, {
                        ...this.quotes[idx],
                        ...quote,
                    })
                }
                this.closeSapDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.v-data-table {
    border: 1px solid #eeeeee;
}
.v-data-table >>> thead {
    background-color: #eeeeee;
}

.doc-validation-cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    column-gap: 4px;
}

.doc-status-icon {
    display: block;
    margin: 0 auto;
}

.doc-status-slot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.doc-info-slot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.doc-link-btn {
    min-width: 20px !important;
    width: 20px;
    height: 20px;
    padding: 0 !important;
    border-radius: 0 !important;
}

.clickable-row {
    cursor: pointer;
}
</style>
